import { Injectable } from '@angular/core';
import { User, UserAttributes } from '@auth/auth/lib/models/user';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  UserAttributeIdentifierEnum,
  UserAttributeKeyEnum
} from 'src/app/enums/enums';

const UserSwMessageType = {
  getUser: 'getUser',
  setUser: 'setUser',
  getUserResponse: 'getUserResponse'
} as const;

/**
 * The user-sw.js service worker allows us to share the user variable across tabs which makes the app appear to load faster.
 *
 * MFEs can use this service to communicate with the user service worker.
 * UserSwService shares the user object between the main thread and the service worker.
 * ServiceWorker data is shared across all tabs.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private static readonly SW_PATH = 'user-sw.js';
  private readonly userSubject = new BehaviorSubject<User | null>(null);

  private getUserListener = (event: MessageEvent<any>) => {
    navigator.serviceWorker?.removeEventListener(
      'message',
      this.getUserListener
    );
    if (event.data.type === UserSwMessageType.getUserResponse) {
      const user = event.data?.user;
      this.userSubject.next(typeof user === 'string' ? JSON.parse(user) : user);
    }
  };

  getUser(): Observable<User | null> {
    Promise.resolve(
      UserService.getServiceWorker()?.then((sw) => {
        if (sw?.active) {
          navigator.serviceWorker?.addEventListener(
            'message',
            this.getUserListener
          );
          sw?.active.postMessage({
            type: UserSwMessageType.getUser
          });
        }
      })
    );
    return this.userSubject.asObservable();
  }

  setUser(user?: User): void {
    if (!navigator.serviceWorker) {
      return;
    }

    UserService.getServiceWorker()?.then((sw) => {
      sw?.active?.postMessage({
        type: UserSwMessageType.setUser,
        user
      });
    });
  }

  clearUser(): void {
    this.setUser();
  }

  static isInternal(user: User): boolean {
    const isExternal = UserService.getValuesByAttribute(
      user,
      UserAttributeIdentifierEnum.USER,
      UserAttributeKeyEnum.isExternal
    );
    return isExternal != true;
  }

  static hasRTDataAccess(user: User): boolean {
    const apps = UserService.getValuesByAttribute(
      user,
      UserAttributeIdentifierEnum.USER,
      UserAttributeKeyEnum.Applications
    ) as string[];
    return !!apps?.some((x) => x === UserAttributeIdentifierEnum.RiskTechData);
  }

  static getValuesByAttribute(
    user: User,
    userAttribute: string,
    key: string
  ): string | boolean | string[] | undefined {
    const attributes = (
      user?.userAttributes as Record<string, UserAttributes[]>
    )?.[userAttribute];
    const value = attributes?.find(
      (x: any) => x.key.toLowerCase() === key.toLowerCase()
    )?.value;
    return value;
  }

  private static getServiceWorker(): Promise<
    ServiceWorkerRegistration | undefined
  > {
    return navigator.serviceWorker?.getRegistration(this.SW_PATH);
  }
}
